import type { Theme, SxProps, Breakpoint } from "@mui/material/styles";
import { useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import { useTheme } from "@mui/material/styles";
import ListItemButton from "@mui/material/ListItemButton";
import Drawer, { drawerClasses } from "@mui/material/Drawer";
import type { Props as SimplebarProps } from "simplebar-react";
import { useLocation } from "react-router-dom";
import type { LinkProps } from "react-router-dom";
import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { WorkspacesPopoverProps } from "../../types/workspace";
import SimpleBar from "simplebar-react";
import { WorkspacesPopover } from "./WorkspacesPopover";
import Footer from "../Footer";
import { RoleComponentGuard } from "../Reuseables/Guards/RoleComponentGuard";
import { Role, Stage } from "../../types/enums";
import { useTranslation } from "react-i18next";
import { Divider } from "@mui/material";
import { CustomAccountInfo } from "../../types/customAccountInfo";
import { useOrganizationContext } from "../ContextProviders/OrganizationContext";
import { useApplicationContext } from "../ContextProviders/ApplicationContext";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import "simplebar-react/dist/simplebar.min.css";
import IconsForProjects from "../Navbar/IconsForProjects";
import { Project } from "../../types/project";
import { getOrganizations } from "../../api/organization";
import { useNavData } from "../Routes/AuthenticatedRoutes";
// ----------------------------------------------------------------------

interface RouterLinkProps extends Omit<LinkProps, "to"> {
  href: string;
}

const RouterLink = forwardRef<HTMLAnchorElement, RouterLinkProps>(
  ({ href, ...other }, ref) => <Link ref={ref} to={href} {...other} />
);
// ----------------------------------------------------------------------
function usePathname() {
  const { pathname } = useLocation();

  return useMemo(() => pathname, [pathname]);
}

export type NavContentProps = {
  data?: SidebarNavItem;
  workspaces?: WorkspacesPopoverProps["data"];
  sx?: SxProps<Theme>;
};

export function NavDesktop({
  sx,
  layoutQuery,
}: NavContentProps & { layoutQuery: Breakpoint }) {
  const theme = useTheme();
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const { showLoading, stopLoading, showSnackbar } = useApplicationContext();
  const {
    activeOrganization: { workspaces },
    setOrganizations,
    setActiveOrganization,
  } = useOrganizationContext();

  useEffect(() => {
    if (isAuthenticated) {
      const account = instance.getActiveAccount() as CustomAccountInfo;
      let role = account?.idTokenClaims?.role;

      const roleEnum: Role | undefined = Role[role as keyof typeof Role];
      async function FetchOrganizations() {
        showLoading("labels.loadingProjects");
        try {
          let organizations = await getOrganizations();
          if (organizations.length > 0) {
            switch (roleEnum) {
              case Role.Partner:
              case Role.LocalCoordinator:
              case Role.SolutionOwner:
              case Role.PrecureAdmin:
            
                setOrganizations(organizations);
              
                var lastOrgUsed: string | null | undefined;
                if (localStorage.getItem("organizationId")) {
                  lastOrgUsed = localStorage.getItem("organizationId");
                } else {
                  lastOrgUsed = organizations[0].id;
                }
                let activeOrganization = organizations.find(
                  (t) => t.id === lastOrgUsed
                );
                if (activeOrganization) {
                  setActiveOrganization(activeOrganization);
                }

               
                break;
            }
          } else {
            showSnackbar("No workspaces found.", "error");
          }
        } catch (error: unknown) {
          if (error instanceof Error) {
            showSnackbar(error.message, "error");
          } else {
            showSnackbar("An unknown error occurred", "error");
          }
        } finally {
          stopLoading();
        }
      }
      FetchOrganizations();
    }
    // eslint-disable-next-line
  }, [isAuthenticated, instance]);

  return (
    <Box
      sx={{
        pt: 2.5,
        px: 2.5,
        top: 0,
        left: 0,
        height: 1,
        display: "none",
        position: "fixed",
        flexDirection: "column",
        // bgcolor: theme.sideNavbar.navbarColor,
        zIndex: 1101,
        width: "300px",
        borderRight:
          theme.palette.mode == "dark"
            ? `1px solid rgba(255, 255, 255, 0.12)`
            : `1px solid rgba(0, 0, 0, 0.12)`,
        [theme.breakpoints.up(layoutQuery)]: {
          display: "flex",
        },
        ...sx,
      }}
    >
      <NavContent data={useNavData()} workspaces={workspaces} />
    </Box>
  );
}

// ----------------------------------------------------------------------

export function NavMobile({
  sx,
  data,
  open,
  onClose,
}: NavContentProps & { open: boolean; onClose: () => void }) {
  const pathname = usePathname();
  const {
    activeOrganization: { workspaces },
  } = useOrganizationContext();
  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          pt: 2.5,
          px: 2.5,
          overflow: "unset",
          width: "320px",
          ...sx,
        },
      }}
    >
      <NavContent data={data} workspaces={workspaces} />
    </Drawer>
  );
}

// ----------------------------------------------------------------------

export function NavContent({ data, workspaces, sx }: NavContentProps) {
  const { activeWorkspace, activeOrganization } = useOrganizationContext();

  return (
    <>
      <Box
        alt="Full logo"
        component="img"
        src={
          activeOrganization && activeOrganization.logo
            ? activeOrganization.logo
            : `https://precure-cdn.azureedge.net/files/mli-interface/logos/precure_full.png`
        }
        width="60%"
        height="32px"
      />
      <RoleComponentGuard roles={[Role.PrecureAdmin, Role.Partner]}>
        {workspaces && workspaces?.length > 0 &&    <WorkspacesPopover data={workspaces} sx={{ mt: 2 }} />}
      </RoleComponentGuard>
      <Scrollbar fillContent sx={{ mt: 2 }}>
        <Box display="flex" flex="1 1 auto" flexDirection="column" sx={sx}>
          <Box gap={0.5} display="flex" flexDirection="column">
            <Divider textAlign="left" sx={{ fontSize: "0.85rem" }}>
              Dashboards
            </Divider>
            {data?.dashboards.map((item) => {
              return (
                <SidebarNavItem
                  key={item.title}
                  title={item.title}
                  roles={item.roles}
                  route={item.route}
                  icon={item.icon}
                />
              );
            })}
            <Divider textAlign="left" sx={{ fontSize: "0.85rem" }}>
              Manage
            </Divider>
            {data?.manage.map((item) => {
              return (
                <SidebarNavItem
                  key={item.title}
                  title={item.title}
                  roles={item.roles}
                  route={item.route}
                  icon={item.icon}
                />
              );
            })}
            <Divider textAlign="left" sx={{ fontSize: "0.85rem" }}>
              Misc
            </Divider>
            {data?.misc.map((item) => {
              return (
                <SidebarNavItem
                  key={item.title}
                  title={item.title}
                  roles={item.roles}
                  route={item.route}
                  icon={item.icon}
                />
              );
            })}
            <Divider textAlign="left" sx={{ fontSize: "0.85rem" }}>
              Ongoing Projects
            </Divider>
            {activeWorkspace &&
              activeWorkspace.projects &&
              activeWorkspace.projects.length > 0 &&
              activeWorkspace.projects
                .filter((p) => p.stage < Stage.Complete)
                .sort((a, b) => a.stage - b.stage)
                .map((project: Project) => (
                  <SidebarNavItem
                    key={project.projectCode}
                    title={project.projectName}
                    roles={[
                      Role.LocalCoordinator,
                      Role.SolutionOwner,
                      Role.PrecureAdmin,
                      Role.Partner,
                    ]}
                    route={`/myProjects/${project.projectCode}`}
                    icon={<IconsForProjects project={project} />}
                  />
                ))}
          </Box>
        </Box>
      </Scrollbar>
      <Footer />
    </>
  );
}

export interface SidebarNavItem {
  home: SideNavItemProps[];
  dashboards: SideNavItemProps[];
  manage: SideNavItemProps[];
  misc: SideNavItemProps[];
}
interface SideNavItemProps {
  roles: Role[];
  route: string;
  icon: React.ReactNode;
  title: string;
  info?: React.ReactNode;
}

const SidebarNavItem: React.FC<SideNavItemProps> = ({
  roles,
  route,
  icon,
  title,
  info,
}) => {
  const pathname = usePathname();
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <RoleComponentGuard roles={roles}>
      <ListItem disableGutters disablePadding>
        <ListItemButton
          disableGutters
          component={RouterLink}
          href={route}
          sx={{
            pl: 2,
            py: 1,
            gap: 2,
            pr: 1.5,
            borderRadius: 0.75,
            typography: "body2",
            fontWeight: "fontWeightMedium",
            minHeight: "44px",
            ...(route === pathname && {
              fontWeight: "fontWeightSemiBold",
              bgcolor: theme.palette.primary.main,
              "&:hover": {
                bgcolor: theme.palette.primary.main,
              },
            }),
          }}
        >
          <Box component="span" sx={{ width: 24, height: 24 }}>
            {icon}
          </Box>

          <Box
            component="span"
            flexGrow={1}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {t(title)}
          </Box>

          {info && info}
        </ListItemButton>
      </ListItem>
    </RoleComponentGuard>
  );
};

type ScrollbarProps = SimplebarProps & {
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  fillContent?: boolean;
  slotProps?: {
    wrapper?: SxProps<Theme>;
    contentWrapper?: SxProps<Theme>;
    content?: Partial<SxProps<Theme>>;
  };
};
const Scrollbar = forwardRef<HTMLDivElement, ScrollbarProps>(
  ({ slotProps, children, fillContent, sx, ...other }, ref) => (
    <Box
      component={SimpleBar}
      scrollableNodeProps={{ ref }}
      clickOnTrack={false}
      className={"mnl__scrollbar__root"}
      sx={{
        minWidth: 0,
        minHeight: 0,
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        "& .simplebar-scrollbar:before": { backgroundColor: "gray" },
        "& .simplebar-scrollbar.simplebar-visible:before": { opacity: 0.48 },
        "& .simplebar-wrapper": slotProps?.wrapper as React.CSSProperties,
        "& .simplebar-content-wrapper":
          slotProps?.contentWrapper as React.CSSProperties,
        "& .simplebar-content": {
          ...(fillContent && {
            minHeight: 1,
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
          }),
          ...slotProps?.content,
        } as React.CSSProperties,
        ...sx,
      }}
      {...other}
    >
      {children}
    </Box>
  )
);
